import { ConversationThread, type Message, type ChannelFilter } from "@/v5/components/ConversationThread";
import { EmailComposeModal } from "@/v5/components/EmailComposeModal";
import { HealthNexusComposeModal } from "@/v5/components/HealthNexusComposeModal";
import { NoteComposeModal } from "@/v5/components/NoteComposeModal";
import { useCommlog, type VoiceCallEntry, type VideoCallEntry } from "@/v5/hooks/useCommlog";
import type { UseVoiceCallReturn } from "@/v5/hooks/useVoiceCall";
import type { UseVideoCallReturn } from "@/v5/hooks/useVideoCall";
import {
  commlogEntryToMessage,
  healthNexusMessageToMessage,
  isCommunicationEntry,
  normalizeTimestamp,
} from "@/v5/lib/commlogMessage";
import { Component, useMemo, useCallback, useState, useEffect, useRef } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { toast } from "@/store/toastStore";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { getErrorMessage } from "@/services/apiClient";
import { formatPhone } from "@/lib/utils";
import { useModuleAccess } from "@/hooks/useAccountModules";
import type { ReadSource } from "@/lib/readSource";

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? "/api/v5";

function resolveRecordingUrl(url?: string | null): string | undefined {
  const value = (url ?? "").trim();
  if (!value) {
    return undefined;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  try {
    const apiOrigin = (() => {
      try {
        return new URL(API_BASE, window.location.origin).origin;
      } catch {
        return window.location.origin;
      }
    })();
    return new URL(value, apiOrigin).toString();
  } catch {
    return undefined;
  }
}

function timestampMs(raw: string): number {
  const parsed = new Date(normalizeTimestamp(raw)).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function lastTenDigits(value?: string | null): string {
  return (value ?? "").replace(/\D/g, "").slice(-10);
}

function voiceCallToMessage(
  vc: VoiceCallEntry,
  idx: number,
  patientName: string,
  patientPhone?: string,
  patientPhoto?: string,
): Message {
  const rawSummary = (vc.summary || "").trim();
  const isMachineSummary = /^Recording duration:.*SID:/i.test(rawSummary);
  const summary = (rawSummary && !isMachineSummary)
    ? rawSummary
    : `Call from ${formatPhone(vc.callFrom)} to ${formatPhone(vc.callTo)}`;
  const patientDigits = lastTenDigits(patientPhone);
  const inbound = patientDigits !== "" && lastTenDigits(vc.callFrom) === patientDigits;
  return {
    id: `vc-${vc.callStreamsID || idx}`,
    content: summary,
    timestamp: normalizeTimestamp(vc.createDate),
    sender: inbound ? "patient" : "provider",
    senderName: inbound ? (patientName || "Voice Call") : "Voice Call",
    senderAvatar: inbound ? patientPhoto : undefined,
    type: "call-recording",
    read: true,
    channel: "phone",
    mediaDuration: vc.duration,
    mediaUrl: resolveRecordingUrl(vc.recordingUrl)
      ?? (vc.callStreamsID ? `/api/v4/api.php/call-recording-play/${vc.callStreamsID}` : undefined),
    callSummary: summary,
    transcript: vc.transcript || undefined,
    canReply: false,
    canEdit: false,
    canDelete: false,
  };
}

function videoCallToMessage(vc: VideoCallEntry, idx: number): Message {
  const durationLabel = vc.durationFormatted || `${vc.duration}s`;
  return {
    id: `vidcall-${idx}`,
    content: `Video call (${durationLabel})`,
    timestamp: normalizeTimestamp(vc.createDate),
    sender: "provider",
    senderName: "Video Call",
    type: "video-recording",
    read: true,
    channel: "video",
    mediaDuration: vc.duration,
    mediaUrl: resolveRecordingUrl(vc.recordingUrl),
    callSummary: vc.recordingMessage || (vc.compositionStatus === "pending"
      ? "Recording is being processed..."
      : vc.compositionStatus === "failed"
        ? "Recording failed"
        : undefined),
    transcript: vc.transcript || undefined,
    canReply: false,
    canEdit: false,
    canDelete: false,
  };
}

type OpenModal = "email" | "note" | "healthnexus" | null;
type DisplayStyle = "threads" | "sequential";
type SortStyle = "newest" | "oldest";

interface CommLogSectionProps {
  patientId: string;
  patientName: string;
  patientPhone?: string;
  patientEmail?: string;
  patientPhoto?: string;
  smsOptIn?: boolean;
  voiceCall: UseVoiceCallReturn;
  videoCall: UseVideoCallReturn;
  highlightMessageId?: string;
  /** Called when the patient's name is clicked in the thread — used to expand the detail panel. */
  onPatientNameClick?: () => void;
  /**
   * The screen hosting this thread. Replies sent here mark the answered message
   * as read, and the read line shows this screen. Required so a new host has to
   * say where it is rather than silently recording replies with no screen.
   */
  readSource: ReadSource;
}

export function CommLogSection({
  patientId,
  patientName,
  patientPhone,
  patientEmail,
  patientPhoto,
  smsOptIn,
  voiceCall,
  videoCall,
  highlightMessageId,
  onPatientNameClick,
  readSource,
}: CommLogSectionProps) {
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);

  // A pending reply belongs to the patient it was picked on. The Comm Center
  // keeps this section mounted across patients, so drop it on a switch, during
  // render, before the next patient's "Send from" can move to its line.
  const [replyPatientId, setReplyPatientId] = useState(patientId);
  if (replyPatientId !== patientId) {
    setReplyPatientId(patientId);
    setReplyToMessage(null);
  }

  // WhatsApp is an opt-in module, so `.explicit` (not `.enabled`) is the right
  // signal: it is false while the module map is loading, false on the persisted
  // authStore seed alone, and false if the refetch fails — i.e. fail-closed.
  // Derived here rather than passed down, matching how showHealthNexus works,
  // so every page mounting this section is covered without touching any of them.
  const { explicit: whatsAppEnabled } = useModuleAccess("whatsapp");

  const {
    entries,
    voiceCalls,
    videoCalls,
    healthNexusEntries,
    healthNexusCanSend,
    loading,
    loadingMore,
    error,
    hasMore,
    whatsAppWindowOpen,
    refetch,
    loadMore,
    sendSms,
    sendEmail,
    sendNote,
    sendWhatsApp,
    sendWhatsAppTemplate,
    sendHealthNexus,
  } = useCommlog(
    patientId,
    "all",
    patientPhone,
    patientEmail,
    readSource,
  );

  const prevVideoStatusRef = useRef(videoCall.status);
  useEffect(() => {
    const prev = prevVideoStatusRef.current;
    prevVideoStatusRef.current = videoCall.status;
    if (videoCall.status === "idle" && prev !== "idle") {
      setTimeout(() => refetch(), 1000);
    }
  }, [videoCall.status, refetch]);

  const messages = useMemo(() => {
    const displayStyle: DisplayStyle = "sequential";
    const sortStyle: SortStyle = "oldest";
    const filteredEntries = entries.filter((entry) =>
      isCommunicationEntry(entry as unknown as Record<string, unknown>)
    );

    const commlogMessages = filteredEntries
      .map((e) => commlogEntryToMessage(
        e as unknown as Record<string, unknown>,
        { hideQuoted: true, patientId, patientName, patientAvatar: patientPhoto },
      ))
      .filter((m): m is Message => m !== null);

    const vcMessages = voiceCalls.flatMap((vc, i) => {
      try { return [voiceCallToMessage(vc, i, patientName, patientPhone, patientPhoto)]; }
      catch { return []; }
    });
    const vidMessages = videoCalls.flatMap((vc, i) => {
      try { return [videoCallToMessage(vc, i)]; }
      catch { return []; }
    });
    const hnMessages = healthNexusEntries.flatMap((m) => {
      try { return [healthNexusMessageToMessage(m, { patientName, patientAvatar: patientPhoto })]; }
      catch { return []; }
    });

    const all = [...commlogMessages, ...vcMessages, ...vidMessages, ...hnMessages].sort((a, b) => {
      const delta = timestampMs(a.timestamp) - timestampMs(b.timestamp);
      return sortStyle === "oldest" ? delta : -delta;
    });

    if (displayStyle === "sequential") {
      return all;
    }

    const childrenByParent = new Map<string, Message[]>();
    const rootMessages: Message[] = [];

    for (const message of all) {
      if (message.replyID) {
        const list = childrenByParent.get(message.replyID) ?? [];
        list.push(message);
        childrenByParent.set(message.replyID, list);
      } else {
        rootMessages.push(message);
      }
    }

    const ordered: Message[] = [];

    const walk = (message: Message, depth = 0) => {
      ordered.push({ ...message, depth });
      const children = childrenByParent.get(message.id) ?? [];
      const sortedChildren = [...children].sort((a, b) => {
        const delta = timestampMs(a.timestamp) - timestampMs(b.timestamp);
        return sortStyle === "oldest" ? delta : -delta;
      });
      sortedChildren.forEach((child) => walk(child, depth + 1));
    };

    rootMessages.forEach((message) => walk(message, 0));

    return ordered;
  }, [entries, healthNexusEntries, patientId, patientName, patientPhone, patientPhoto, videoCalls, voiceCalls]);

  const handleCancelReply = useCallback(() => {
    setReplyToMessage(null);
  }, []);

  const handleReply = useCallback((message: Message) => {
    if (message.canReply === false) {
      return;
    }
    setReplyToMessage(message);
    switch (message.channel) {
      case "email":
        setOpenModal("email");
        break;
      case "note":
      case "phone":
      case "video":
        setOpenModal("note");
        break;
      // sms and whatsapp stay inline — ConversationThread shows the reply indicator
    }
  }, []);

  /**
   * Send one of the practice's approved WhatsApp messages.
   *
   * Gated on the module the same way the free-text path is: an account without
   * WhatsApp never gets the affordance, and the server refuses it regardless.
   */
  const handleSendWhatsAppTemplate = useCallback(
    async (contentSid: string, variables: Record<string, string>) => {
      if (!whatsAppEnabled) return;
      await sendWhatsAppTemplate(contentSid, variables);
      toast.success("WhatsApp message sent");
    },
    [sendWhatsAppTemplate, whatsAppEnabled],
  );

  // Resolves false when the send failed, so the composer keeps what was typed:
  // a refusal such as "send from a different number" is only useful if the
  // message is still there to resend.
  const handleSendMessage = useCallback(
    async (
      content: string,
      channel: ChannelFilter,
      replyID?: string,
      recipients?: string[],
      fromNumber?: string,
    ): Promise<boolean | void> => {
      try {
        switch (channel) {
          case "sms":
            await sendSms(content, replyID, recipients, fromNumber);
            toast.success("SMS sent");
            break;
          case "whatsapp":
            // Belt-and-braces against a stale render: the composer is already
            // hidden when the module is off, and the server refuses anyway.
            if (!whatsAppEnabled) return;
            await sendWhatsApp(content, replyID, recipients);
            toast.success("WhatsApp message sent");
            break;
          default:
            return;
        }
        setReplyToMessage(null);
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to send"));
        return false;
      }
    },
    [sendSms, sendWhatsApp, whatsAppEnabled],
  );

  const handleSendEmail = useCallback(
    async (
      subject: string,
      htmlBody: string,
      replyID?: string,
      recipients?: string[],
      files?: File[],
      existingDocuments?: string[],
    ) => {
      await sendEmail(
        subject,
        htmlBody,
        replyID,
        recipients,
        files,
        existingDocuments,
      );
      setReplyToMessage(null);
    },
    [sendEmail],
  );

  const handleSendNote = useCallback(
    async (content: string, replyID?: string, recipients?: string[], subject?: string) => {
      await sendNote(content, replyID, recipients, subject);
      setReplyToMessage(null);
    },
    [sendNote], 
  );

  const handleModalClose = useCallback((v: boolean) => {
    if (!v) {
      setOpenModal(null);
      setReplyToMessage(null);
    }
  }, []);

  const emailReplyTo = openModal === "email" && replyToMessage?.channel === "email"
    ? {
        subject: replyToMessage.subject || "",
        htmlBody: replyToMessage.htmlContent || replyToMessage.content,
        replyID: replyToMessage.id,
      }
    : undefined;

  const noteReplyTo = openModal === "note" && replyToMessage
    ? { replyID: replyToMessage.id, preview: replyToMessage.content }
    : undefined;

  if (loading && entries.length === 0 && voiceCalls.length === 0 && videoCalls.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && entries.length === 0 && voiceCalls.length === 0 && videoCalls.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Failed to load communication log</p>
        <button
          onClick={() => { void refetch(); }}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <ConversationThread
        patientName={patientName}
        patientPhone={patientPhone}
        patientId={patientId}
        smsOptIn={smsOptIn}
        messages={messages}
        onSendMessage={handleSendMessage}
        onOpenCall={() => {
          if (voiceCall.isOnCall) {
            voiceCall.expand();
          } else {
            void voiceCall.initialize();
            voiceCall.openCallPanel(patientId, patientName, patientPhone);
          }
        }}
        onOpenEmail={() => setOpenModal("email")}
        onOpenVideo={() => {
          if (videoCall.status === "idle") {
            videoCall.requestVideoCall(patientId, patientName);
          } else {
            videoCall.expand();
          }
        }}
        onOpenNote={() => setOpenModal("note")}
        onOpenHealthNexus={() => setOpenModal("healthnexus")}
        showHealthNexus={healthNexusCanSend}
        showWhatsApp={whatsAppEnabled}
        whatsAppWindowOpen={whatsAppWindowOpen}
        onSendWhatsAppTemplate={whatsAppEnabled ? handleSendWhatsAppTemplate : undefined}
        onReply={handleReply}
        onPatientNameClick={onPatientNameClick}
        replyTo={replyToMessage}
        onCancelReply={handleCancelReply}
        activeModal={openModal}
        onLoadOlder={loadMore}
        hasOlder={hasMore}
        loadingOlder={loadingMore}
        highlightMessageId={highlightMessageId}
      />

      <EmailComposeModal
        open={openModal === "email"}
        onOpenChange={handleModalClose}
        patientName={patientName}
        patientEmail={patientEmail}
        patientId={patientId}
        onSend={handleSendEmail}
        replyTo={emailReplyTo}
      />

      <NoteComposeModal
        open={openModal === "note"}
        onOpenChange={handleModalClose}
        patientName={patientName}
        onSend={handleSendNote}
        replyTo={noteReplyTo}
      />

      <HealthNexusComposeModal
        open={openModal === "healthnexus"}
        onOpenChange={handleModalClose}
        patientName={patientName}
        onSend={sendHealthNexus}
      />
    </>
  );
}

interface CommLogErrorBoundaryState {
  hasError: boolean;
}

class CommLogErrorBoundaryInner extends Component<
  CommLogSectionProps & { children?: ReactNode },
  CommLogErrorBoundaryState
> {
  state: CommLogErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): CommLogErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("CommLogSection crashed:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
          <AlertCircle className="h-10 w-10 text-destructive/60" />
          <p className="text-sm text-muted-foreground">Something went wrong loading the communication log.</p>
          <button
            onClick={() => this.setState({ hasError: false })}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      );
    }
    return <CommLogSection {...this.props} />;
  }
}

export { CommLogErrorBoundaryInner as CommLogSectionWithBoundary };
